import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { reportResponse } from '../interfaces/apis/reports';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { SingleColumn, TableData } from '../components/table/table.interface';
import { ConfigService } from './config.service';

export type CustomerCoverageReportType = 'covered' | 'uncovered';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private reportsBaseUrl: string = `${environment.apiBaseUrl}/visits/reports/`;

  private getDateRangeParams(from: Date, to: Date): HttpParams {
    const fromStr = this.configService.formatDateTimeForApi(from);
    const toStr = this.configService.formatDateTimeForApi(to, true);

    return new HttpParams().set('from', fromStr).set('to', toStr);
  }

  /**
   * Retrieves dashboard report URL from a configured API path.
   * @param path Report endpoint path relative to the API base URL
   * @param from Start date of the report
   * @param to End date of the report
   * @returns Observable with the report URL
   */
  getDashboardReport(
    path: string,
    from: Date,
    to: Date,
  ): Observable<reportResponse> {
    const params = this.getDateRangeParams(from, to);

    return this.http.get<reportResponse>(`${environment.apiBaseUrl}${path}`, {
      params,
    });
  }

  /**
   * Downloads a report from the given URL
   * @param reportURL The URL of the report to download
   */
  downloadReport(reportURL: string): void {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = reportURL;
    link.download = this.extractFilenameFromURL(reportURL);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Retrieves product visit comments report
   * @param productId The product ID
   * @param from Start date
   * @param to End date
   * @returns Observable with the report URL
   */
  getProductVisitCommentsReport(
    productId: string,
    from: Date,
    to: Date,
  ): Observable<reportResponse> {
    const fromStr = this.configService.formatDateTimeForApi(from);
    const toStr = this.configService.formatDateTimeForApi(to, true);

    const params = new HttpParams().set('from', fromStr).set('to', toStr);

    return this.http.get<reportResponse>(
      `${this.reportsBaseUrl}${productId}/visit-comments`,
      {
        params,
      },
    );
  }

  /**
   * Retrieves product SFE report
   * @param productId The product ID
   * @param from Start date
   * @param to End date
   * @returns Observable with the report URL
   */
  getProductSFEReport(
    productId: string,
    from: Date,
    to: Date,
  ): Observable<reportResponse> {
    const fromStr = this.configService.formatDateTimeForApi(from);
    const toStr = this.configService.formatDateTimeForApi(to, true);

    const params = new HttpParams().set('from', fromStr).set('to', toStr);

    return this.http.get<reportResponse>(
      `${this.reportsBaseUrl}${productId}/sfe`,
      {
        params,
      },
    );
  }

  /**
   * Retrieves customer coverage report URL
   * @param reportType Covered or uncovered customer report
   * @param from Start date
   * @param to End date
   * @returns Observable with the report URL
   */
  getCustomerCoverageReport(
    reportType: CustomerCoverageReportType,
    from: Date,
    to: Date,
    userId?: string,
  ): Observable<reportResponse> {
    const fromStr = this.configService.formatDateTimeForApi(from);
    const toStr = this.configService.formatDateTimeForApi(to, true);

    const params = new HttpParams().set('from', fromStr).set('to', toStr);
    const baseUrl = `${this.reportsBaseUrl}customer-coverage/${reportType}`;
    const url = userId ? `${baseUrl}/${userId}` : baseUrl;

    return this.http.get<reportResponse>(url, {
      params,
    });
  }

  /**
   * Extracts filename from a URL
   * @param url The URL to extract filename from
   * @returns The filename
   */
  private extractFilenameFromURL(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
      return decodeURIComponent(filename);
    } catch {
      return 'report.xlsx';
    }
  }

  private formatGroupCell(
    cell: unknown,
    groupColumns?: SingleColumn[],
  ): string {
    if (Array.isArray(cell)) return cell.join(', ');
    if (!cell || typeof cell !== 'object') return '';

    const cellRecord = cell as Record<string, string>;
    const values = groupColumns?.length
      ? groupColumns.map((groupColumn) => cellRecord[groupColumn.name])
      : Object.values(cellRecord);

    return values
      .filter((value) => value !== null && value !== undefined && value !== '')
      .join(', ');
  }

  /**
   * Exports table data to Excel file
   * @param data The table data to export
   * @param tableName The name of the table for the filename
   */
  public exportTableData(data: TableData, tableName: string): void {
    const exportHeads = data.heads
      .filter((head) => !head.ignore)
      .map((head) => ({
        ...head,
        groupColumns: head.groupColumns?.filter(
          (groupColumn) => !groupColumn.ignore,
        ),
      }));

    // Converting table map to excel work sheet
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([
      exportHeads.map((head) => head.name).flat(),
      ...data.rows.map((row, rowIndex) => {
        return exportHeads
          .map((col) => {
            let cell = row.value[col.name];

            if (cell === null || cell === undefined) return '';
            if (typeof cell === 'number') return cell;
            if (!cell) return '';

            return typeof cell === 'string' && col.type === 'single'
              ? cell
              : Array.isArray(cell) && col.type === 'group'
                ? this.formatGroupCell(cell, col.groupColumns)
                : typeof cell === 'object' && col.type === 'single'
                  ? cell
                  : typeof cell === 'object' && col.type === 'group'
                    ? this.formatGroupCell(cell, col.groupColumns)
                    : col.type === 'index'
                      ? rowIndex + 1
                      : cell;
          })
          .flat();
      }),
    ]);

    /* generate excel file including the worksheet */
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tableName);

    // Forming filename and appending time settings and creation date to it
    let filename = '';
    const from = this.configService.from();
    const to = this.configService.to();
    if (from && to) {
      filename =
        tableName +
        ' - ' +
        moment(from.toISOString()).format('dddd, MMMM Do YYYY') +
        ' To ' +
        moment(to.toISOString()).format('dddd, MMMM Do YYYY') +
        ' - Created On ' +
        moment(Date.now()).format('dddd, MMMM Do YYYY h:mm:ss a');
    } else {
      filename =
        tableName +
        ' - ' +
        moment(Date.now()).format('dddd, MMMM Do YYYY h:mm:ss a');
    }

    /* save to file */
    XLSX.writeFile(wb, filename + '.xlsx');
  }
}
